import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEvents as EmojiEventsIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import {
  createReferral,
  fetchReferralStatus,
  collectReferralPrize,
  fetchReferralStats,
  fetchRewardHistory,
  clearNotification,
} from '../../../store/Slices/referralSlice';

const ReferralPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const userId = useSelector((state) => state.user.data._id);
  const userReferralCode = useSelector((state) => state.user.data.referralCode || 'YOUR_CODE');
  const userReferrer = useSelector((state) => state.user.data.referredBy || null);
  const {
    referralStatus = null,
    referralStats = null,
    rewardHistory = [],
    notifications = [],
    loading = false,
    error = null,
  } = useSelector((state) => state.referral || {});
  const [referralCode, setReferralCode] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [tabValue, setTabValue] = useState(0);

  // Fetch data on mount
  useEffect(() => {
    if (userId) {
      dispatch(fetchReferralStatus(userId));
      dispatch(fetchReferralStats(userId));
      dispatch(fetchRewardHistory(userId));
    }
  }, [dispatch, userId]);

  // Handle Referral Form Submission
  const handleCreateReferral = async (e) => {
    e.preventDefault();
    if (referralCode && userId) {
      try {
        const result = await dispatch(
          createReferral({ user_id: userId, referral_code: referralCode })
        ).unwrap();
        setReferralCode('');
        setSnackbarMessage(result.message || 'Referral created successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        // Fetch updated referral data
        dispatch(fetchReferralStatus(userId));
        dispatch(fetchReferralStats(userId));
        dispatch(fetchRewardHistory(userId));
      } catch (error) {
        setSnackbarMessage(error || 'Failed to create referral');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  // Handle Collect Prize
  const handleCollectPrize = async () => {
    try {
      const result = await dispatch(collectReferralPrize(userId)).unwrap();
      setSnackbarMessage(result.message || 'Reward collected successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      dispatch(fetchReferralStatus(userId));
      dispatch(fetchReferralStats(userId));
      dispatch(fetchRewardHistory(userId));
    } catch (error) {
      setSnackbarMessage(error || 'Failed to collect prize');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle Copy Referral Link
  const handleCopyReferralLink = () => {
    navigator.clipboard.writeText(`https://yourplatform.com/register?ref=${userReferralCode}`);
    setSnackbarMessage('Referral link copied to clipboard!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  // Handle Notification Close
  const handleCloseNotification = (id) => {
    dispatch(clearNotification(id));
  };

  // Handle Snackbar Close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Handle Tab Change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (!userId) {
    return (
      <Container sx={{ textAlign: 'center', py: 10, bgcolor: 'background.default' }}>
        <Typography variant="h6" color="error">
          Please log in to access the referral program.
        </Typography>
      </Container>
    );
  }

  if (loading && !referralStatus) {
    return (
      <Container sx={{ textAlign: 'center', py: 10, bgcolor: 'background.default' }}>
        <CircularProgress color="primary" />
        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading referral data...
        </Typography>
      </Container>
    );
  }

  if (error && !referralStatus) {
    return (
      <Container sx={{ textAlign: 'center', py: 10, bgcolor: 'background.default' }}>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? '#181A20' : '#FFFFFF',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2, color: theme.palette.text.primary }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, textAlign: 'center', color: theme.palette.text.primary }}
          >
            Referral
          </Typography>
          <Box sx={{ width: 48 }} />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4, color: 'text.primary' }}>
        {notifications.map((notification) => (
          <Snackbar
            key={notification.id}
            open={true}
            autoHideDuration={6000}
            onClose={() => handleCloseNotification(notification.id)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              severity={notification.type}
              action={
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => handleCloseNotification(notification.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
              sx={{ width: '100%', bgcolor: 'background.paper', color: 'text.primary' }}
            >
              {notification.message}
            </Alert>
          </Snackbar>
        ))}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbarSeverity} onClose={handleSnackbarClose} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? '#1F2329' : '#FFFFFF',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                transition: 'transform 0.3s',
                '&:hover': { transform: 'translateY(-4px)' },
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonAddIcon sx={{ fontSize: 32, color: '#F0B90B' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Pending Referrals
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#F0B90B' }}>
                    {referralStatus?.counts?.pending || 0}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? '#1F2329' : '#FFFFFF',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                transition: 'transform 0.3s',
                '&:hover': { transform: 'translateY(-4px)' },
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 32, color: '#F0B90B' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Eligible Referrals
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#F0B90B' }}>
                    {referralStatus?.counts?.eligible || 0}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? '#1F2329' : '#FFFFFF',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                transition: 'transform 0.3s',
                '&:hover': { transform: 'translateY(-4px)' },
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <EmojiEventsIcon sx={{ fontSize: 32, color: '#F0B90B' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Completed Rewards
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#F0B90B' }}>
                    {referralStatus?.counts?.completed || 0}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? '#1F2329' : '#FFFFFF',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.divider}`,
            mb: 4,
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, color: 'text.primary' }}
            >
              Invite Friends
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Share your referral code or link to invite friends and earn rewards.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <TextField
                value={userReferralCode}
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  style: { color: 'text.primary' },
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F0B90B' },
                  },
                }}
              />
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyReferralLink}
                sx={{
                  bgcolor: '#F0B90B',
                  color: '#181A20',
                  '&:hover': { bgcolor: '#D4A009' },
                  flexShrink: 0,
                }}
              >
                Copy Link
              </Button>
            </Box>
            {userReferrer ? (
              <Box
                sx={{
                  p: 2,
                  bgcolor: theme.palette.mode === 'dark' ? '#181A20' : '#F9F9F9',
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <PersonAddIcon sx={{ fontSize: 28, color: '#F0B90B' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    You were invited by a friend!
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
                  >
                    {userReferrer.username}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#F0B90B' }}>
                    Referral Code: {userReferrer.referralCode}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <form onSubmit={handleCreateReferral}>
                <TextField
                  label="Enter Referral Code"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  required
                  InputLabelProps={{ style: { color: 'text.secondary' } }}
                  InputProps={{
                    style: { color: 'text.primary' },
                    sx: {
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F0B90B' },
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    mt: 2,
                    bgcolor: '#F0B90B',
                    color: '#181A20',
                    '&:hover': { bgcolor: '#D4A009' },
                  }}
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Submit Referral'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleCollectPrize}
            disabled={loading || (referralStatus?.counts?.eligible || 0) < 20}
            sx={{
              bgcolor: '#F0B90B',
              color: '#181A20',
              '&:hover': { bgcolor: '#D4A009' },
              transition: 'all 0.3s',
            }}
          >
            {loading ? 'Collecting...' : 'Collect Reward'}
          </Button>
        </Box>

        <Card
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? '#1F2329' : '#FFFFFF',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.divider}`,
            mb: 4,
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, color: 'text.primary' }}
            >
              Referral Statistics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Total Referrals
                </Typography>
                <Typography variant="h5" sx={{ color: '#F0B90B' }}>
                  {referralStats?.totalReferrals || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Total Trade Amount
                </Typography>
                <Typography variant="h5" sx={{ color: '#F0B90B' }}>
                  {referralStats?.totalTradeAmount || 0} USDT
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Total Rewards Earned
                </Typography>
                <Typography variant="h5" sx={{ color: '#F0B90B' }}>
                  {referralStats?.totalRewardsEarned || 0} USDT
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? '#1F2329' : '#FFFFFF',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.divider}`,
            mb: 4,
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, color: 'text.primary' }}
            >
              Reward History
            </Typography>
            {rewardHistory?.length > 0 ? (
              <Paper
                sx={{
                  overflowX: 'auto',
                  bgcolor: 'transparent',
                  border: 'none',
                }}
              >
                <Table sx={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          color: 'text.primary',
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          py: 1,
                        }}
                      >
                        Reward Amount
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'text.primary',
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          py: 1,
                        }}
                      >
                        Eligible Referrals
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'text.primary',
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          py: 1,
                        }}
                      >
                        Collected At
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rewardHistory.map((reward, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': { bgcolor: theme.palette.action.hover },
                        }}
                      >
                        <TableCell
                          sx={{
                            color: 'text.primary',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            py: 1,
                          }}
                        >
                          {reward.reward_amount} USDT
                        </TableCell>
                        <TableCell
                          sx={{
                            color: 'text.primary',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            py: 1,
                          }}
                        >
                          {reward.eligible_count}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: 'text.primary',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            py: 1,
                          }}
                        >
                          {new Date(reward.collected_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No rewards collected yet.
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? '#1F2329' : '#FFFFFF',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              bgcolor: 'transparent',
              borderBottom: `1px solid ${theme.palette.divider}`,
              '& .MuiTab-root': {
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 500,
              },
              '& .Mui-selected': {
                color: '#F0B90B',
              },
              '& .MuiTabs-indicator': {
                bgcolor: '#F0B90B',
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="How It Works" />
            <Tab label="Rules" />
            <Tab label="FAQ" />
          </Tabs>
          <CardContent sx={{ pt: 3 }}>
            {tabValue === 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Our referral program allows you to earn rewards by inviting new users to our
                  platform. By referring friends, you can earn significant USDT rewards based on the
                  number of eligible referrals.
                </Typography>
              </Box>
            )}
            {tabValue === 1 && (
              <Box>
                <ul style={{ paddingLeft: '20px', color: theme.palette.text.secondary }}>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Enter a Referral Code:</strong> Use the form above to enter the Referral
                      Code of the person who invited you. Your referral starts as "Pending."
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Eligibility Criteria:</strong> Your referral becomes "Eligible" when you
                      complete a trade (Spot, Limit, or AI Trade) worth at least 50 USDT within 30
                      days of the referral creation.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Collect Rewards:</strong> Once your referrer has enough eligible
                      referrals, they can collect:
                      <ul style={{ paddingLeft: '20px' }}>
                        <li>20 Eligible Referrals: 500 USDT</li>
                        <li>50 Eligible Referrals: 1,500 USDT</li>
                        <li>100 Eligible Referrals: 2,500 USDT</li>
                      </ul>
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Reward Collection:</strong> Your referrer can click the "Collect Reward"
                      button to add the reward to their Spot Wallet in USDT.
                    </Typography>
                  </li>
                </ul>
              </Box>
            )}
            {tabValue === 2 && (
              <Box>
                <ul style={{ paddingLeft: '20px', color: theme.palette.text.secondary }}>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      You cannot refer yourself.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Duplicate referrals are not allowed.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Referrals must complete their qualifying trade within 30 days.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Rewards are credited in USDT and are non-transferable.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Abuse of the referral system (e.g., fake accounts) will result in account
                      suspension.
                    </Typography>
                  </li>
                </ul>
              </Box>
            )}
            {tabValue === 3 && (
              <Box sx={{ color: 'text.secondary' }}>
                <Typography variant="body2">
                  <strong>Q:</strong> How do I know if my referral is eligible?
                </Typography>
                <Typography variant="body2">
                  <strong>A:</strong> Check the "Eligible Referrals" count above. You’ll be notified
                  when a referral becomes eligible.
                </Typography>
                <Typography variant="body2">
                  <strong>Q:</strong> Can I collect rewards multiple times?
                </Typography>
                <Typography variant="body2">
                  <strong>A:</strong> Yes, as long as you accumulate new eligible referrals.
                </Typography>
                <Typography variant="body2">
                  <strong>Q:</strong> Where are my rewards credited?
                </Typography>
                <Typography variant="body2">
                  <strong>A:</strong> Rewards are credited to your Spot Wallet in USDT.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ReferralPage;